import { css } from '@linaria/atomic';
import { theme } from '@frontend/theme';

/**
 * Linaria styles for the Accordion.
 *
 * Because Linaria extracts CSS at build time, prop-driven styling is expressed as
 * static class blocks (and enum-keyed maps) that consumers compose with `cx`.
 * @linaria/atomic resolves same-property conflicts by last-class-wins, so orthogonal
 * concerns (variant, active state, disabled, focus ring, …) can be layered safely.
 * Continuous/runtime values (e.g. `distance`, `padding`) are applied via the inline
 * `style` prop at the call site rather than through custom CSS variables.
 */

/* -------------------------------------------------------------------------- */
/* Default header (title / subtitle)                                          */
/* -------------------------------------------------------------------------- */

export const defaultHeaderContainerStyle = css`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

export const defaultHeaderTitleStyle = css`
  font-size: ${theme.fontSize(18)};
  font-weight: ${theme.font.weight.bold};
  margin-bottom: 0;
`;

export const defaultHeaderTitleWithSubtitleStyle = css`
  margin-bottom: ${theme.spacing(0.5)};
`;

export const defaultHeaderSubtitleStyle = css`
  margin: 0;
  max-width: 500px;
  font-size: ${theme.fontSize(14)};
  color: ${theme.colors.neutral40};
`;

/* -------------------------------------------------------------------------- */
/* Accordion header                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Borders are written as deep longhands (border-*-color/style/width) on purpose:
 * @linaria/atomic only raises longhands to priority 2, so they reliably win over
 * NakedButton's `border: none` shorthand (priority 1). Using `border`/`border-bottom`
 * shorthands here would tie on specificity and fall back to stylesheet source order,
 * which let NakedButton's `border: none` bleed `currentColor` (black) through and
 * caused a border flash while `transition` animated it on open.
 */
export const headerBaseStyle = css`
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  border-top-style: none;
  border-right-style: none;
  border-left-style: none;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: transparent;
  background-color: white;
  transition: border-bottom-color 0.3s ease, background-color 0.3s ease;
`;

export const headerChevronPositionStyles = {
  left: css`
    flex-direction: row;
  `,
  right: css`
    flex-direction: row-reverse;
  `,
};

/** Variant styling that does not depend on the active state. */
export const headerVariantStyles = {
  default: css``,
  blank: css``,
  bordered: css``,
  error: css``,
  filled: css`
    background-color: ${theme.colors.neutral5};
  `,
  hybrid: css`
    border-bottom-style: none;
    border-radius: ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0;
  `,
  location: css`
    border-bottom-style: none;
  `,
  main: css`
    border-bottom-style: none;
  `,
};

/** Applied when the item is active. */
export const headerActiveStyles = {
  default: css`
    background-color: white;
  `,
  blank: css`
    background-color: white;
  `,
  bordered: css`
    background-color: white;
  `,
  error: css`
    background-color: white;
  `,
  filled: css`
    background-color: ${theme.colors.neutral5};
  `,
  hybrid: css`
    background-color: white;
    border-top-width: 1px;
    border-top-style: solid;
    border-top-color: ${theme.colors.neutral20};
    border-right-width: 1px;
    border-right-style: solid;
    border-right-color: ${theme.colors.neutral20};
    border-left-width: 1px;
    border-left-style: solid;
    border-left-color: ${theme.colors.neutral20};
  `,
  location: css`
    background-color: ${theme.colors.primary20};
    color: ${theme.colors.primary70};
    svg {
      color: ${theme.colors.primary70};
    }
  `,
  main: css`
    background-color: ${theme.colors.neutral5};
  `,
};

/** Overrides the location active colors when the item spans multiple locations. */
export const headerActiveMultiLocationStyle = css`
  background-color: ${theme.colors.warning5};
  color: ${theme.colors.warning80};
  svg {
    color: ${theme.colors.warning80};
  }
`;

/** Applied when the item is inactive. */
export const headerInactiveStyles = {
  default: css`
    background-color: white;
  `,
  blank: css`
    background-color: white;
  `,
  bordered: css`
    background-color: white;
  `,
  error: css`
    background-color: ${theme.colors.critical5};
  `,
  filled: css`
    background-color: ${theme.colors.neutral5};
  `,
  hybrid: css`
    background-color: ${theme.colors.neutral5};
    border-top-width: 1px;
    border-top-style: solid;
    border-top-color: ${theme.colors.neutral5};
    border-right-width: 1px;
    border-right-style: solid;
    border-right-color: ${theme.colors.neutral5};
    border-left-width: 1px;
    border-left-style: solid;
    border-left-color: ${theme.colors.neutral5};
  `,
  location: css`
    background-color: white;
  `,
  main: css`
    background-color: white;
  `,
};

/** Solid bottom border, layered last so it wins over the base/variant border-bottom. */
export const headerBorderBottomStyle = css`
  border-bottom-style: solid;
  border-bottom-color: ${theme.colors.neutral20};
`;

/** Error variant always tints its label text. */
export const headerErrorSpanStyle = css`
  span {
    color: ${theme.colors.status.critical};
  }
`;

export const headerDisabledStyle = css`
  cursor: unset;
  span {
    color: ${theme.colors.neutral20};
  }
`;

/** Focus ring for non-interactive headers (the whole header is the control). */
export const headerFocusRingStyle = css`
  &::before {
    content: '';
    position: absolute;
    pointer-events: none;
    top: 0px;
    left: 0px;
    border: 2px solid ${theme.colors.primary50};
    border-radius: ${theme.borderRadius.medium};
    width: 100%;
    height: 100%;
    z-index: ${theme.zIndex.high};
    opacity: 0;
    transition: 0.2s ease;
  }
  &:focus-visible {
    outline: none;
    ::before {
      opacity: 1;
    }
  }
`;

export const headingWrapperStyle = css`
  width: 100%;
  height: 100%;
  all: unset;
  display: flex;
`;

export const headerContentContainerStyle = css`
  flex: 1;
  display: flex;
  width: 100%;
  overflow: hidden;
`;

/* -------------------------------------------------------------------------- */
/* Chevron                                                                     */
/* -------------------------------------------------------------------------- */

export const chevronContainerStyle = css`
  height: ${theme.spacing(5)};
  padding: ${theme.spacing(1)};
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
`;

export const chevronContainerDisabledStyle = css`
  svg {
    color: ${theme.colors.neutral20};
  }
`;

export const chevronMarginStyles = {
  left: css`
    margin-left: ${theme.spacing(1)};
  `,
  right: css`
    margin-right: ${theme.spacing(1)};
  `,
};

export const chevronAnimatedStyle = css`
  transition: transform 300ms ease-out;
  display: flex;
  flex-direction: center;
  justify-content: center;
  transform: rotate(0deg);
`;

export const chevronAnimatedOpenStyle = css`
  transform: rotate(180deg);
`;

/* -------------------------------------------------------------------------- */
/* Icon / location headers                                                     */
/* -------------------------------------------------------------------------- */

export const iconHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  overflow: hidden;
`;

export const flexShrinkNoneStyle = css`
  flex-shrink: 0;
`;

export const iconHeaderTitleStyle = css`
  margin: 0;
  color: inherit;
`;

export const inheritColorStyle = css`
  color: inherit;
`;

/* -------------------------------------------------------------------------- */
/* Accordion item                                                             */
/* -------------------------------------------------------------------------- */

export const itemBaseStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const itemVariantStyles = {
  default: css`
    border-radius: ${theme.spacing(1)};
  `,
  error: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.status.critical};
  `,
  location: css`
    border-radius: ${theme.borderRadius.medium};
  `,
  hybrid: css`
    border-radius: ${theme.borderRadius.medium};
  `,
  main: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
  `,
  bordered: css`
    border: 1px solid ${theme.colors.neutral20};
    &:first-child {
      border-radius: ${theme.spacing(1, 1, 0, 0)};
      border-bottom: none;
    }
    &:last-child {
      border-radius: ${theme.spacing(0, 0, 1, 1)};
      border-top: none;
    }
  `,
  blank: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    &:last-child {
      border-bottom: none;
    }
  `,
  filled: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    &:last-child {
      border-bottom: none;
    }
  `,
};

export const itemBoxShadowStyle = css`
  box-shadow: ${theme.shadows.light};
`;

/* -------------------------------------------------------------------------- */
/* Accordion body                                                             */
/* -------------------------------------------------------------------------- */

export const bodyBaseStyle = css`
  overflow: hidden;
`;

export const bodyVariantStyles = {
  hybrid: css`
    border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
    border-right: 1px solid ${theme.colors.neutral20};
    border-bottom: 1px solid ${theme.colors.neutral20};
    border-left: 1px solid ${theme.colors.neutral20};
    border-top: none;
  `,
  location: css`
    background: white;
  `,
};

export const bodyContentStyle = css`
  padding: ${theme.spacing(2)};
`;
